import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

const STORAGE_KEY = 'atlasdraft-pending-draft';
const DEBUG = import.meta.env.DEV;

// Debug logging helper
const log = (emoji: string, message: string, data?: any) => {
  if (!DEBUG) return;
  const style = 'color: #6366f1; font-weight: bold;';
  if (data) {
    console.log(`%c[DraftProtection] ${emoji} ${message}`, style, data);
  } else {
    console.log(`%c[DraftProtection] ${emoji} ${message}`, style);
  }
};

export interface DraftData {
  sectionId: string;
  chapterId: string;
  projectId: string;
  title: string;
  content: string;
  savedAt: string; // ISO timestamp
}

interface UseDraftProtectionOptions {
  sectionId?: string;
  chapterId?: string;
  projectId?: string;
  onSyncSuccess?: () => void;
  onSyncFailure?: (error: Error) => void;
}

export function useDraftProtection(options: UseDraftProtectionOptions = {}) {
  const { sectionId, chapterId, projectId, onSyncSuccess, onSyncFailure } = options;
  const isSyncingRef = useRef(false);

  // Save draft to localStorage
  const saveDraftLocally = useCallback((title: string, content: string) => {
    if (!sectionId || !chapterId || !projectId) return false;

    const draft: DraftData = {
      sectionId,
      chapterId,
      projectId,
      title,
      content,
      savedAt: new Date().toISOString(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
      log('💾', 'Saved draft locally', { 
        sectionId, 
        title: title.substring(0, 30) + (title.length > 30 ? '...' : ''),
        contentLength: content.length,
        savedAt: draft.savedAt 
      });
      return true;
    } catch (error) {
      console.error('Failed to save draft locally:', error);
      return false;
    }
  }, [sectionId, chapterId, projectId]);

  // Get pending draft from localStorage
  const getPendingDraft = useCallback((): DraftData | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      const draft = JSON.parse(stored) as DraftData;
      log('📖', 'Retrieved pending draft', { sectionId: draft.sectionId, savedAt: draft.savedAt });
      return draft;
    } catch {
      return null;
    }
  }, []);

  // Check if there's a pending draft for the current section
  const hasPendingDraftForCurrentSection = useCallback((): boolean => {
    const draft = getPendingDraft();
    return draft !== null && draft.sectionId === sectionId;
  }, [getPendingDraft, sectionId]);

  // Clear draft from localStorage - ONLY call after confirmed sync
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      log('🗑️', 'Draft cleared from localStorage');
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  }, []);

  // Sync draft to Supabase
  const syncDraft = useCallback(async (draft?: DraftData): Promise<boolean> => {
    const draftToSync = draft || getPendingDraft();
    if (!draftToSync) return false;
    if (isSyncingRef.current) {
      log('⏳', 'Sync already in progress, skipping');
      return false;
    }

    isSyncingRef.current = true;
    log('🔄', 'Starting sync for section', { sectionId: draftToSync.sectionId });

    try {
      // Update section in database
      const { error } = await supabase
        .from('sections')
        .update({
          title: draftToSync.title,
          content: draftToSync.content,
          updated_at: new Date().toISOString(),
        })
        .eq('id', draftToSync.sectionId);

      if (error) {
        throw error;
      }

      // ONLY clear after confirmed success
      clearDraft();
      log('✅', 'Sync successful');
      onSyncSuccess?.();
      return true;
    } catch (error) {
      log('❌', 'Sync failed', error);
      console.error('Failed to sync draft:', error);
      onSyncFailure?.(error as Error);
      return false;
    } finally {
      isSyncingRef.current = false;
    }
  }, [getPendingDraft, clearDraft, onSyncSuccess, onSyncFailure]);

  // Check for pending draft on mount and restore if matches current section
  const restoreFromLocalDraft = useCallback((): { title: string; content: string } | null => {
    const draft = getPendingDraft();
    if (draft && draft.sectionId === sectionId) {
      log('🔄', 'Restoring draft for current section', { sectionId });
      return { title: draft.title, content: draft.content };
    }
    return null;
  }, [getPendingDraft, sectionId]);

  return {
    saveDraftLocally,
    getPendingDraft,
    hasPendingDraftForCurrentSection,
    clearDraft,
    syncDraft,
    restoreFromLocalDraft,
  };
}

// Standalone functions for use outside of React components
export function getPendingDraftStatic(): DraftData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as DraftData;
  } catch {
    return null;
  }
}

export function clearDraftStatic() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    log('🗑️', 'Draft cleared from localStorage (static)');
  } catch (error) {
    console.error('Failed to clear draft:', error);
  }
}

export async function syncDraftStatic(draft: DraftData): Promise<boolean> {
  log('🔄', 'Starting static sync for section', { sectionId: draft.sectionId });
  
  try {
    const { error } = await supabase
      .from('sections')
      .update({
        title: draft.title,
        content: draft.content,
        updated_at: new Date().toISOString(),
      })
      .eq('id', draft.sectionId);

    if (error) {
      throw error;
    }

    // ONLY clear after confirmed success
    clearDraftStatic();
    log('✅', 'Static sync successful');
    return true;
  } catch (error) {
    log('❌', 'Static sync failed', error);
    console.error('Failed to sync draft:', error);
    return false;
  }
}
